import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { AssessmentOverviewAdapter } from './sections/assessment-overview/adapter'
import { AuthorizationAdapter } from './sections/authorization/adapter'
import { PlanOfCareAdapter } from './sections/planOfCare/adapter'
import { FinalStepsAdapter } from './sections/final-steps/adapter'
import { VisitSchedulingAdapter } from './sections/visit-scheduling/adapter'
import { ADLNeedsSectionAdapter } from './sections/adls-needs/adapter'
import { MedicationsAdapter } from './sections/medications/adapter'
import { ResumptionOverviewAdapter } from './sections/resumption-overview/adapter'
import { SupervisoryReviewAdapter } from './sections/supervisory-review/adapter'
import { VitalsAdapter } from './sections/vitals/adapter'
import { RocFormData } from './types/RocFormData'
import { SkilledNeedsSectionAdapter } from './sections/skilled-needs/adapter'
import { MedicalBackgroundAdapter } from './sections/medical-background/adapter'
import { DischargePlanningAdapter } from './sections/discharge-planning/adapter'
import { NarrativeSectionAdapter } from './sections/narrative/adapter'
import { mutateFlatAnswers } from '../helpers'
import { AssessmentType } from '../../Clinical/Assessments/constants'
import { AdapterOptions, SectionAdapter } from '../adapters/types'
import { BaseAssessmentAdapter } from '../adapters/BaseAssessmentAdapter'
import { BaseSubsectionAdapter } from '../adapters/BaseSubsectionAdapter'

/**
 * Array of all ROC section adapter instances
 */
const ROC_SECTION_ADAPTERS: (BaseSubsectionAdapter<RocFormData> | SectionAdapter<RocFormData>)[] = [
  new AssessmentOverviewAdapter(),
  new SupervisoryReviewAdapter(),
  new VitalsAdapter(),
  new DischargePlanningAdapter(),
  new AuthorizationAdapter(),
  new VisitSchedulingAdapter(),
  new ADLNeedsSectionAdapter(),
  new PlanOfCareAdapter(),
  new MedicationsAdapter(),
  new FinalStepsAdapter(),
  new ResumptionOverviewAdapter(),
  new SkilledNeedsSectionAdapter(),
  new MedicalBackgroundAdapter(),
  new NarrativeSectionAdapter()
]

export class RocAssessmentAdapter extends BaseAssessmentAdapter<
  RocFormData & Record<string, unknown>
> {
  readonly assessmentType = AssessmentType.ROC

  protected flatAnswersToFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    options?: AdapterOptions
  ): RocFormData & Record<string, unknown> {
    // Use composable section adapters for clean, maintainable extraction
    const formData: Partial<RocFormData> = {}

    // Extract data from all section adapters dynamically
    for (const adapter of ROC_SECTION_ADAPTERS) {
      const sectionData = adapter.extractFormData(flatAnswers, options)
      Object.assign(formData, sectionData)
    }

    return formData as RocFormData & Record<string, unknown>
  }

  protected formDataToFlatAnswers(
    formData: RocFormData,
    currentFlatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Record<string, QuestionnaireResponseItem> {
    // Use composable section adapters for clean, maintainable transformation
    const allMutations: Array<{ linkId: string; value: unknown }> = []

    // Build mutations from all section adapters dynamically
    for (const adapter of ROC_SECTION_ADAPTERS) {
      if (adapter instanceof BaseSubsectionAdapter) {
        adapter.setFormData(formData)
        allMutations.push(...adapter.buildFlatAnswers())
      } else if (adapter instanceof SectionAdapter) {
        allMutations.push(...adapter.buildFlatAnswers(formData))
      }
    }

    // Apply all mutations using existing helper
    mutateFlatAnswers(currentFlatAnswers, allMutations)

    return currentFlatAnswers
  }
}
